import {
  context,
  isSpanContextValid,
  SpanStatusCode,
  trace,
} from "@opentelemetry/api";

import type { Span } from "@opentelemetry/api";
import type { IdentityAttempt } from "@/lib/wallet/identity-diagnostics";

const spans = new WeakMap<IdentityAttempt, Span>();

export function startIdentityTrace(attempt: IdentityAttempt): void {
  try {
    const span = trace.getTracer("mudwater-web").startSpan("wallet.identity", {
      attributes: { ...attempt, flow: "wallet_identity" },
    });
    spans.set(attempt, span);
    const ids = span.spanContext();
    if (isSpanContextValid(ids)) {
      attempt.trace_id = ids.traceId;
      attempt.span_id = ids.spanId;
    }
  } catch {
    // Identity continues when tracing is unavailable.
  }
}

export function identityTracePhase(
  attempt: IdentityAttempt,
  phase: string,
  fields: Record<string, string>
): void {
  try {
    const span = spans.get(attempt);
    span?.setAttributes({ ...attempt, ...fields, phase });
    span?.addEvent(phase, fields);
    if (
      (phase === "failed" && fields.failure_class !== "user_rejected") ||
      fields.outcome === "refused" ||
      fields.outcome === "invalid"
    )
      span?.setStatus({ code: SpanStatusCode.ERROR });
  } catch {
    // Structured fields only; never record the original wallet exception.
  }
}

export function endIdentityTrace(attempt: IdentityAttempt): void {
  const span = spans.get(attempt);
  spans.delete(attempt);
  try {
    span?.end();
  } catch {
    // Tracing never alters the completed proof.
  }
}

/** Explicit per-attempt context survives awaits without a global current wallet. */
export function identityFetch(
  attempt: IdentityAttempt,
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  let called = false;
  let request: Promise<Response> | undefined;
  let fetchError: unknown;
  const send = () => {
    called = true;
    try {
      request = globalThis.fetch(input, init);
      return request;
    } catch (error) {
      fetchError = error;
      throw error;
    }
  };
  try {
    const span = spans.get(attempt);
    return span
      ? context.with(trace.setSpan(context.active(), span), send)
      : send();
  } catch (error) {
    // A tracing failure before invocation may fall back; a request is never repeated.
    if (request) return request;
    if (!called) return send();
    throw fetchError ?? error;
  }
}
